import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { authService } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';

const LINK_VALID_SECONDS = 15 * 60;

const STEP_EMAIL = 'email';
const STEP_OTP   = 'otp';

export default function ForgotPasswordPage() {
  const [step, setStep]   = useState(STEP_EMAIL);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(LINK_VALID_SECONDS);
  const timerRef = useRef(null);

  // Countdown timer for link validity (step 2)
  useEffect(() => {
    if (step === STEP_OTP) {
      setSecondsLeft(LINK_VALID_SECONDS);
      timerRef.current = setInterval(() => {
        setSecondsLeft((s) => {
          if (s <= 1) { clearInterval(timerRef.current); return 0; }
          return s - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [step]);

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  // ── Step 1: submit email ──────────────────────────────────────────────────
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email) { setError('Ingresa tu correo electrónico.'); return; }
    setIsLoading(true);
    try {
      await authService.forgotPassword(email);
    } catch {
      // Always advance regardless — prevent email enumeration
    } finally {
      setIsLoading(false);
    }
    setStep(STEP_OTP);
  };

  const handleResend = async () => {
    setError('');
    try {
      await authService.forgotPassword(email);
    } catch {
      // silent — always show "sent" to prevent enumeration
    }
    setSecondsLeft(LINK_VALID_SECONDS);
  };

  // ── Step 2: check email for reset link ───────────────────────────────────
  if (step === STEP_OTP) {
    return (
      <div className="min-h-screen bg-[#0a0e17] flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-[#1a2332] rounded-lg p-8 border border-[#d4af37]/30 text-center">
          <div className="w-16 h-16 bg-[#d4af37]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-[#d4af37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-[#f5f5f0] mb-3">Revisa tu correo</h1>
          <p className="text-[#b0b0b0] mb-2">Enviamos un enlace de restablecimiento a</p>
          <p className="text-[#f5f5f0] font-medium mb-6">{email}</p>
          <p className="text-sm text-[#b0b0b0] mb-6">
            Haz clic en el enlace del correo para continuar.
            Válido por{' '}
            <span className="text-[#d4af37] font-mono font-bold">{formatTime(secondsLeft)}</span>.
          </p>

          {secondsLeft === 0 && (
            <button
              onClick={handleResend}
              className="mb-4 w-full text-sm text-[#d4af37] hover:underline"
            >
              Reenviar enlace
            </button>
          )}

          <button
            onClick={() => { setStep(STEP_EMAIL); setError(''); }}
            className="text-sm text-[#b0b0b0] hover:text-[#d4af37] underline block mx-auto"
          >
            ← Cambiar correo
          </button>
        </div>
      </div>
    );
  }

  // ── Step 1: email form ────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#0a0e17] flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-[#1a2332] rounded-lg p-8 border border-[#2a3444]">
        <h1 className="text-3xl font-bold text-[#f5f5f0] mb-2">¿Olvidaste tu contraseña?</h1>
        <p className="text-[#b0b0b0] mb-8">
          Ingresa tu correo y te enviaremos un enlace para restablecer tu contraseña.
        </p>

        {error && (
          <Alert className="mb-6 border-red-500 bg-red-500/10">
            <AlertDescription className="text-red-400">{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleEmailSubmit} className="space-y-4">
          <div>
            <Label htmlFor="email" className="text-[#f5f5f0]">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@email.com"
              className="bg-[#0a0e17] border-[#2a3444] text-[#f5f5f0]"
              autoFocus
            />
          </div>
          <Button
            type="submit"
            disabled={isLoading}
            className="w-full bg-[#d4af37] text-[#0a0e17] hover:bg-[#e5c158] font-semibold"
          >
            {isLoading ? 'Enviando…' : 'Enviar enlace de recuperación'}
          </Button>
        </form>

        <p className="text-center text-[#b0b0b0] mt-6">
          <Link to="/login" className="text-[#d4af37] hover:underline">
            ← Volver al login
          </Link>
        </p>
      </div>
    </div>
  );
}
